import { useState, useEffect } from 'react';
import AdminLayout from '@/components/AdminLayout';
import { getCaseStudies, createCaseStudy, updateCaseStudy, deleteCaseStudy } from '@/lib/api';
import { toast } from 'sonner';
import { Plus, Edit2, Trash2, Loader2, Eye, EyeOff, Star, X } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { format } from 'date-fns';
import { SERVICES } from '@/lib/services';

const emptyForm = {
  title: '',
  slug: '',
  client: '',
  industry: '',
  excerpt: '',
  content: '',
  cover_image: '',
  gallery: '',
  services: [],
  metrics: [{ label: '', value: '' }],
  tags: '',
  challenge: '',
  solution: '',
  results: '',
  testimonial_quote: '',
  testimonial_author: '',
  duration: '',
  year: String(new Date().getFullYear()),
  featured: false,
  published: true,
};

export default function AdminCaseStudies() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState(emptyForm);

  useEffect(() => { load(); }, []);

  const load = async () => {
    try {
      const res = await getCaseStudies({ publishedOnly: false });
      setItems(res.data);
    } catch (err) {
      toast.error('Failed to load case studies');
    } finally {
      setLoading(false);
    }
  };

  const slugify = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');

  const handleTitleChange = (e) => {
    const title = e.target.value;
    setFormData({ ...formData, title, slug: editing ? formData.slug : slugify(title) });
  };

  const toggleService = (slug) => {
    setFormData((f) => ({
      ...f,
      services: f.services.includes(slug) ? f.services.filter((s) => s !== slug) : [...f.services, slug],
    }));
  };

  const updateMetric = (idx, field, value) => {
    setFormData((f) => ({ ...f, metrics: f.metrics.map((m, i) => i === idx ? { ...m, [field]: value } : m) }));
  };
  const addMetric = () => setFormData((f) => ({ ...f, metrics: [...f.metrics, { label: '', value: '' }] }));
  const removeMetric = (idx) => setFormData((f) => ({ ...f, metrics: f.metrics.filter((_, i) => i !== idx) }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = {
      ...formData,
      tags: formData.tags.split(',').map((t) => t.trim()).filter(Boolean),
      gallery: formData.gallery.split('\n').map((u) => u.trim()).filter(Boolean),
      metrics: formData.metrics.filter((m) => m.label && m.value),
    };
    try {
      if (editing) {
        await updateCaseStudy(editing.id, payload);
        toast.success('Case study updated');
      } else {
        await createCaseStudy(payload);
        toast.success('Case study created');
      }
      load();
      setDialogOpen(false);
      reset();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save case study');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (cs) => {
    setEditing(cs);
    setFormData({
      title: cs.title || '',
      slug: cs.slug || '',
      client: cs.client || '',
      industry: cs.industry || '',
      excerpt: cs.excerpt || '',
      content: cs.content || '',
      cover_image: cs.cover_image || '',
      gallery: (cs.gallery || []).join('\n'),
      services: cs.services || [],
      metrics: cs.metrics?.length ? cs.metrics : [{ label: '', value: '' }],
      tags: (cs.tags || []).join(', '),
      challenge: cs.challenge || '',
      solution: cs.solution || '',
      results: cs.results || '',
      testimonial_quote: cs.testimonial_quote || '',
      testimonial_author: cs.testimonial_author || '',
      duration: cs.duration || '',
      year: cs.year || String(new Date().getFullYear()),
      featured: !!cs.featured,
      published: cs.published !== false,
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this case study?')) return;
    try {
      await deleteCaseStudy(id);
      toast.success('Deleted');
      load();
    } catch (err) {
      toast.error('Failed to delete');
    }
  };

  const reset = () => { setEditing(null); setFormData(emptyForm); };

  const inputCls = "w-full bg-[#121212] border border-[#333] focus:border-[#ff5a1f] text-white px-4 py-3 text-sm outline-none";

  return (
    <AdminLayout title="Case Studies">
      <div className="space-y-6" data-testid="admin-case-studies">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-syne text-2xl font-bold text-white">Case Studies</h1>
            <p className="text-[#888] text-sm">Create and publish proof-rich case studies for SEO</p>
          </div>

          <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) reset(); }}>
            <DialogTrigger asChild>
              <button data-testid="add-case-study-btn"
                className="bg-[#ff5a1f] text-white px-4 py-2 text-sm font-semibold flex items-center gap-2 hover:bg-[#e04e1a] transition-colors">
                <Plus className="w-4 h-4" /> New Case Study
              </button>
            </DialogTrigger>
            <DialogContent className="bg-[#0A0A0A] border-[#1F1F1F] text-white max-w-3xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="font-syne text-xl">{editing ? 'Edit Case Study' : 'New Case Study'}</DialogTitle>
              </DialogHeader>

              <form onSubmit={handleSubmit} className="space-y-4 mt-4" data-testid="case-study-form">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-[#888] text-sm mb-2">Title *</label>
                    <input type="text" value={formData.title} onChange={handleTitleChange} required className={inputCls} placeholder="Scaling X from Y to Z" />
                  </div>
                  <div>
                    <label className="block text-[#888] text-sm mb-2">Slug *</label>
                    <input type="text" value={formData.slug} onChange={(e) => setFormData({ ...formData, slug: e.target.value })} required className={inputCls} placeholder="scaling-x-y-to-z" />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-[#888] text-sm mb-2">Client *</label>
                    <input type="text" value={formData.client} onChange={(e) => setFormData({ ...formData, client: e.target.value })} required className={inputCls} placeholder="Client name" />
                  </div>
                  <div>
                    <label className="block text-[#888] text-sm mb-2">Industry</label>
                    <input type="text" value={formData.industry} onChange={(e) => setFormData({ ...formData, industry: e.target.value })} className={inputCls} placeholder="D2C Beauty / B2B SaaS / Fintech" />
                  </div>
                </div>

                <div>
                  <label className="block text-[#888] text-sm mb-2">Services involved *</label>
                  <div className="flex flex-wrap gap-2">
                    {SERVICES.map((s) => (
                      <button key={s.slug} type="button" onClick={() => toggleService(s.slug)}
                        className={`px-3 py-2 text-xs font-medium rounded-full transition-colors border ${formData.services.includes(s.slug) ? 'bg-[#ff5a1f] text-white border-[#ff5a1f]' : 'border-[#333] text-[#888] hover:text-white'}`}>
                        {s.title}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-[#888] text-sm mb-2">Excerpt (used for cards + SEO meta) *</label>
                  <textarea value={formData.excerpt} onChange={(e) => setFormData({ ...formData, excerpt: e.target.value })} required rows={2}
                    className={`${inputCls} resize-none`} placeholder="One-line summary that hooks. Used as meta description." />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-[#888] text-sm mb-2">Cover image URL *</label>
                    <input type="url" value={formData.cover_image} onChange={(e) => setFormData({ ...formData, cover_image: e.target.value })} required className={inputCls} placeholder="https://..." />
                  </div>
                  <div>
                    <label className="block text-[#888] text-sm mb-2">Gallery URLs (one per line)</label>
                    <textarea value={formData.gallery} onChange={(e) => setFormData({ ...formData, gallery: e.target.value })} rows={2} className={`${inputCls} resize-none`} placeholder="https://...\nhttps://..." />
                  </div>
                </div>

                <div>
                  <label className="block text-[#888] text-sm mb-2">Metrics (proof points)</label>
                  <div className="space-y-2">
                    {formData.metrics.map((m, i) => (
                      <div key={i} className="flex gap-2 items-center">
                        <input type="text" value={m.value} onChange={(e) => updateMetric(i, 'value', e.target.value)}
                          className={inputCls + " flex-1"} placeholder="5.4x" />
                        <input type="text" value={m.label} onChange={(e) => updateMetric(i, 'label', e.target.value)}
                          className={inputCls + " flex-[2]"} placeholder="Avg. ROAS lift" />
                        {formData.metrics.length > 1 && (
                          <button type="button" onClick={() => removeMetric(i)} className="text-[#888] hover:text-red-500 p-2"><X className="w-4 h-4" /></button>
                        )}
                      </div>
                    ))}
                    <button type="button" onClick={addMetric} className="text-[#ff5a1f] text-sm font-semibold">+ Add metric</button>
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="block text-[#888] text-sm mb-2">Duration</label>
                    <input type="text" value={formData.duration} onChange={(e) => setFormData({ ...formData, duration: e.target.value })} className={inputCls} placeholder="9 months" />
                  </div>
                  <div>
                    <label className="block text-[#888] text-sm mb-2">Year</label>
                    <input type="text" value={formData.year} onChange={(e) => setFormData({ ...formData, year: e.target.value })} className={inputCls} placeholder="2026" />
                  </div>
                  <div>
                    <label className="block text-[#888] text-sm mb-2">Tags (comma sep.)</label>
                    <input type="text" value={formData.tags} onChange={(e) => setFormData({ ...formData, tags: e.target.value })} className={inputCls} placeholder="D2C, Growth" />
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-4">
                  <div>
                    <label className="block text-[#888] text-sm mb-2">Challenge</label>
                    <textarea value={formData.challenge} onChange={(e) => setFormData({ ...formData, challenge: e.target.value })} rows={3} className={`${inputCls} resize-none`} placeholder="What problem were we solving?" />
                  </div>
                  <div>
                    <label className="block text-[#888] text-sm mb-2">Solution</label>
                    <textarea value={formData.solution} onChange={(e) => setFormData({ ...formData, solution: e.target.value })} rows={3} className={`${inputCls} resize-none`} placeholder="What did we do?" />
                  </div>
                  <div>
                    <label className="block text-[#888] text-sm mb-2">Results</label>
                    <textarea value={formData.results} onChange={(e) => setFormData({ ...formData, results: e.target.value })} rows={3} className={`${inputCls} resize-none`} placeholder="What was the outcome?" />
                  </div>
                  <div>
                    <label className="block text-[#888] text-sm mb-2">Long-form content (optional, supports HTML/markdown-lite)</label>
                    <textarea value={formData.content} onChange={(e) => setFormData({ ...formData, content: e.target.value })} rows={5} className={`${inputCls} resize-none`} placeholder="Deeper narrative for the case study body" />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-[#888] text-sm mb-2">Testimonial quote</label>
                    <textarea value={formData.testimonial_quote} onChange={(e) => setFormData({ ...formData, testimonial_quote: e.target.value })} rows={2} className={`${inputCls} resize-none`} placeholder="They didn't just market our brand — they engineered our growth." />
                  </div>
                  <div>
                    <label className="block text-[#888] text-sm mb-2">Testimonial author</label>
                    <input type="text" value={formData.testimonial_author} onChange={(e) => setFormData({ ...formData, testimonial_author: e.target.value })} className={inputCls} placeholder="Name, role, company" />
                  </div>
                </div>

                <div className="flex items-center gap-6">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={formData.featured} onChange={(e) => setFormData({ ...formData, featured: e.target.checked })} className="w-4 h-4 accent-[#ff5a1f]" />
                    <span className="text-white text-sm">Featured</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={formData.published} onChange={(e) => setFormData({ ...formData, published: e.target.checked })} className="w-4 h-4 accent-[#ff5a1f]" />
                    <span className="text-white text-sm">Published</span>
                  </label>
                </div>

                <div className="flex gap-3 pt-4">
                  <button type="button" onClick={() => { setDialogOpen(false); reset(); }} className="flex-1 border border-[#333] text-white px-4 py-3 text-sm font-semibold hover:bg-white/5">
                    Cancel
                  </button>
                  <button type="submit" disabled={saving} className="flex-1 bg-[#ff5a1f] text-white px-4 py-3 text-sm font-semibold hover:bg-[#e04e1a] flex items-center justify-center gap-2 disabled:opacity-50">
                    {saving && <Loader2 className="w-4 h-4 animate-spin" />}
                    {editing ? 'Update' : 'Publish'}
                  </button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 text-[#ff5a1f] animate-spin" /></div>
        ) : items.length > 0 ? (
          <div className="space-y-4">
            {items.map((cs) => (
              <div key={cs.id} className="bg-[#0A0A0A] border border-[#1F1F1F] p-4 flex gap-4" data-testid={`case-study-item-${cs.id}`}>
                <div className="w-32 h-20 flex-shrink-0 bg-[#121212] overflow-hidden">
                  {cs.cover_image && <img src={cs.cover_image} alt={cs.title} className="w-full h-full object-cover" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-4">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-syne font-bold text-white truncate">{cs.title}</h3>
                        {cs.featured && <Star className="w-3 h-3 text-[#ff5a1f] fill-[#ff5a1f] flex-shrink-0" />}
                      </div>
                      <p className="text-[#888] text-sm line-clamp-1">{cs.client} · {cs.excerpt}</p>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      {cs.published ? (
                        <span className="text-green-500 text-xs flex items-center gap-1"><Eye className="w-3 h-3" /> Published</span>
                      ) : (
                        <span className="text-yellow-500 text-xs flex items-center gap-1"><EyeOff className="w-3 h-3" /> Draft</span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center justify-between mt-3">
                    <span className="text-[#555] text-xs">
                      {cs.created_at && format(new Date(cs.created_at), 'MMM d, yyyy')} · /work/{cs.slug}
                    </span>
                    <div className="flex gap-2">
                      <button onClick={() => handleEdit(cs)} className="text-[#888] hover:text-[#ff5a1f] p-1"><Edit2 className="w-4 h-4" /></button>
                      <button onClick={() => handleDelete(cs.id)} className="text-[#888] hover:text-red-500 p-1"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="bg-[#0A0A0A] border border-[#1F1F1F] p-16 text-center">
            <p className="text-[#888] mb-4">No case studies yet</p>
            <button onClick={() => setDialogOpen(true)} className="text-[#ff5a1f] font-semibold hover:underline">Publish your first case study</button>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
